import { useState } from 'react';
import { api, ApiError, formatDate, formatDateTime, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import {
  REFERRAL_PRIORITIES,
  type Appointment,
  type ChecklistItem,
  type Facility,
  type Page,
  type Referral,
  type ReferralPriority,
  type ReferralTrack,
} from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

const STATES = ['REFERRED', 'TRIAGED', 'SCHEDULED', 'SEEN', 'BOARD_REVIEW', 'FINANCE_APPROVAL', 'LOGISTICS', 'TREATMENT', 'FOLLOWUP', 'REJECTED'];

/**
 * Referral queue — packages/modules/m24-ehealth/src/routes/referrals.ts.
 * STANDARD track: REFERRED -> TRIAGED -> SCHEDULED -> SEEN.
 * OVERSEAS_TREATMENT track: REFERRED -> BOARD_REVIEW -> FINANCE_APPROVAL ->
 * LOGISTICS -> TREATMENT -> FOLLOWUP. REJECTED is a shared terminal exit.
 */
export function Referrals() {
  const { persona } = useAuth();
  const isClinician = persona?.roles.includes('CLINICIAN') ?? false;
  const canView = (isClinician || persona?.roles.includes('RECEPTION') || persona?.roles.includes('TREATMENT_BOARD')) ?? false;

  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [track, setTrack] = useState('');
  const [q, setQ] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  const qs = new URLSearchParams({ limit: '50' });
  if (status) qs.set('status', status);
  if (priority) qs.set('priority', priority);
  if (track) qs.set('track', track);
  if (q) qs.set('q', q);
  const listQ = useApiQuery<Page<Referral>>(`/api/v1/m24/referrals?${qs.toString()}`);

  return (
    <div>
      <PageHeader
        title="Referral Queue"
        subtitle="Specialist referrals — standard track and the overseas-treatment board/finance/logistics track."
        actions={
          isClinician ? (
            <button className="btn-primary" onClick={() => setShowCreate(true)}>
              + New referral
            </button>
          ) : undefined
        }
      />

      {!canView ? (
        <div className="mb-4">
          <ErrorNote message="Viewing the referral queue requires CLINICIAN, RECEPTION or TREATMENT_BOARD." />
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap gap-2">
        <input className="field-input !w-48" placeholder="Search NIN…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="field-input !w-44" value={track} onChange={(e) => setTrack(e.target.value)}>
          <option value="">All tracks</option>
          <option value="STANDARD">Standard</option>
          <option value="OVERSEAS_TREATMENT">Overseas treatment</option>
        </select>
        <select className="field-input !w-40" value={priority} onChange={(e) => setPriority(e.target.value)}>
          <option value="">All priorities</option>
          {REFERRAL_PRIORITIES.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
        <select className="field-input !w-48" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All states</option>
          {STATES.map((s) => (
            <option key={s} value={s}>
              {s.replaceAll('_', ' ')}
            </option>
          ))}
        </select>
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No referrals match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Patient (NIN)</th>
                <th className="table-head">To specialty</th>
                <th className="table-head">Priority</th>
                <th className="table-head">Track</th>
                <th className="table-head">State</th>
                <th className="table-head" />
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((r) => (
                <tr key={r.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetailId(r.id)}>
                  <td className="table-cell font-medium">{r.nin}</td>
                  <td className="table-cell">{r.toSpecialty}</td>
                  <td className="table-cell">
                    <StatusBadge status={r.priority} />
                  </td>
                  <td className="table-cell">
                    <StatusBadge status={r.track} />
                  </td>
                  <td className="table-cell">
                    <StatusBadge status={r.state} />
                  </td>
                  <td className="table-cell text-right">
                    <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setDetailId(r.id)}>
                      Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate ? (
        <CreateReferralModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}

      {detailId ? (
        <ReferralDetailModal
          id={detailId}
          onClose={() => setDetailId(null)}
          onChanged={() => listQ.reload()}
        />
      ) : null}
    </div>
  );
}

function CreateReferralModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const facilitiesQ = useApiQuery<Page<Facility>>('/api/v1/m24/facilities?limit=100');
  const [nin, setNin] = useState('');
  const [fromFacilityId, setFromFacilityId] = useState('');
  const [toSpecialty, setToSpecialty] = useState('');
  const [priority, setPriority] = useState<ReferralPriority>('ROUTINE');
  const [track, setTrack] = useState<ReferralTrack>('STANDARD');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m24/referrals', { nin, fromFacilityId, toSpecialty, priority, track });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to create referral');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New referral" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Patient NIN">
          <input className="field-input" value={nin} onChange={(e) => setNin(e.target.value)} placeholder="e.g. SC870512033" />
        </Field>
        <Field label="Referring facility">
          <select className="field-input" value={fromFacilityId} onChange={(e) => setFromFacilityId(e.target.value)}>
            <option value="">Select a facility…</option>
            {(facilitiesQ.data?.items ?? []).map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Referred to specialty">
          <input className="field-input" value={toSpecialty} onChange={(e) => setToSpecialty(e.target.value)} placeholder="e.g. Cardiology" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Priority">
            <select className="field-input" value={priority} onChange={(e) => setPriority(e.target.value as ReferralPriority)}>
              {REFERRAL_PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Track">
            <select className="field-input" value={track} onChange={(e) => setTrack(e.target.value as ReferralTrack)}>
              <option value="STANDARD">Standard</option>
              <option value="OVERSEAS_TREATMENT">Overseas treatment</option>
            </select>
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={busy || nin.trim().length < 5 || !fromFacilityId || toSpecialty.trim().length < 2}
          >
            {busy ? 'Creating…' : 'Create referral'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ReferralDetailModal({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const detailQ = useApiQuery<Referral>(`/api/v1/m24/referrals/${id}`);
  const { persona } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const isClinician = persona?.roles.includes('CLINICIAN') ?? false;
  const isBoard = persona?.roles.includes('TREATMENT_BOARD') ?? false;
  const canSchedule = isClinician || (persona?.roles.includes('RECEPTION') ?? false);

  const run = async (path: string, body?: unknown) => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m24/referrals/${id}${path}`, body);
      detailQ.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const r = detailQ.data;

  return (
    <Modal title="Referral" onClose={onClose} wide>
      {detailQ.loading || !r ? (
        <Spinner />
      ) : (
        <div className="space-y-4">
          {error ? <ErrorNote message={error} /> : null}
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={r.track} />
            <StatusBadge status={r.priority} />
            <StatusBadge status={r.state} />
          </div>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
            <div className="flex justify-between">
              <dt className="text-slate-500">Patient NIN</dt>
              <dd className="font-medium">{r.nin}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">To specialty</dt>
              <dd className="font-medium">{r.toSpecialty}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">From facility</dt>
              <dd className="font-medium">{r.fromFacility?.name ?? r.fromFacilityId}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Created</dt>
              <dd className="font-medium">{formatDateTime(r.createdAt)}</dd>
            </div>
          </dl>

          {r.track === 'STANDARD' ? (
            <StandardTrackPanel referral={r} canSchedule={canSchedule} isClinician={isClinician} busy={busy} run={run} />
          ) : (
            <OverseasTrackPanel referral={r} isBoard={isBoard} isClinician={isClinician} busy={busy} run={run} />
          )}
        </div>
      )}
    </Modal>
  );
}

function StandardTrackPanel({
  referral,
  canSchedule,
  isClinician,
  busy,
  run,
}: {
  referral: Referral;
  canSchedule: boolean;
  isClinician: boolean;
  busy: boolean;
  run: (path: string, body?: unknown) => Promise<void>;
}) {
  const apptsQ = useApiQuery<Page<Appointment>>(
    referral.state === 'TRIAGED' ? `/api/v1/m24/appointments?q=${encodeURIComponent(referral.nin)}&status=BOOKED&limit=20` : null,
  );
  const [appointmentId, setAppointmentId] = useState('');

  return (
    <div className="border-t border-slate-100 pt-3">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Standard track actions</div>
      {referral.state === 'REFERRED' ? (
        <button className="btn-primary" onClick={() => run('/triage')} disabled={busy || !isClinician}>
          Triage
        </button>
      ) : null}
      {referral.state === 'TRIAGED' ? (
        <div className="space-y-2">
          <Field label="Link an existing BOOKED appointment for this patient">
            <select className="field-input" value={appointmentId} onChange={(e) => setAppointmentId(e.target.value)}>
              <option value="">Select an appointment…</option>
              {(apptsQ.data?.items ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.specialty} — {formatDateTime(a.slot?.startsAt)}
                </option>
              ))}
            </select>
          </Field>
          {(apptsQ.data?.items.length ?? 0) === 0 && !apptsQ.loading ? (
            <p className="text-xs text-slate-400">
              No BOOKED appointments found for {referral.nin} yet — the citizen books one from the facility's open slots, then it appears here.
            </p>
          ) : null}
          <button className="btn-primary" onClick={() => run('/schedule', { appointmentId })} disabled={busy || !canSchedule || !appointmentId}>
            Schedule
          </button>
        </div>
      ) : null}
      {referral.state === 'SEEN' || referral.state === 'REJECTED' ? (
        <p className="text-sm text-slate-500">This referral has reached its terminal state.</p>
      ) : null}
      {referral.state === 'SCHEDULED' ? (
        <button className="btn-primary" onClick={() => run('/seen')} disabled={busy || !isClinician}>
          Mark seen
        </button>
      ) : null}
    </div>
  );
}

function OverseasTrackPanel({
  referral,
  isBoard,
  isClinician,
  busy,
  run,
}: {
  referral: Referral;
  isBoard: boolean;
  isClinician: boolean;
  busy: boolean;
  run: (path: string, body?: unknown) => Promise<void>;
}) {
  const oc = referral.overseasCase;
  const [notes, setNotes] = useState('');
  const [destinationCountry, setDestinationCountry] = useState('');
  const [destinationFacility, setDestinationFacility] = useState('');
  const [amount, setAmount] = useState('');
  const [departureDate, setDepartureDate] = useState(oc?.travelDepartureDate?.slice(0, 10) ?? '');
  const [returnDate, setReturnDate] = useState(oc?.travelReturnDate?.slice(0, 10) ?? '');
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [checklist, setChecklist] = useState<ChecklistItem[]>(oc?.logisticsChecklist ?? []);
  const [followupNote, setFollowupNote] = useState('');

  if (!oc) return <ErrorNote message="Overseas treatment case data is missing for this referral." />;

  const saveLogistics = (next: ChecklistItem[]) => {
    setChecklist(next);
    void run('/overseas/logistics', { checklist: next });
  };

  return (
    <div className="border-t border-slate-100 pt-3">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Overseas treatment track</div>

      <dl className="mb-3 grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-slate-600">
        {oc.destinationCountry ? (
          <div className="flex justify-between">
            <dt className="text-slate-400">Destination</dt>
            <dd className="font-medium">
              {oc.destinationFacility}, {oc.destinationCountry}
            </dd>
          </div>
        ) : null}
        {oc.boardDecision ? (
          <div className="flex justify-between">
            <dt className="text-slate-400">Board decision</dt>
            <dd className="font-medium">{oc.boardDecision}</dd>
          </div>
        ) : null}
        {oc.financingAmount != null ? (
          <div className="flex justify-between">
            <dt className="text-slate-400">Financing</dt>
            <dd className="font-medium">{scr(oc.financingAmount)}</dd>
          </div>
        ) : null}
        {oc.travelDepartureDate ? (
          <div className="flex justify-between">
            <dt className="text-slate-400">Travel</dt>
            <dd className="font-medium">
              {formatDate(oc.travelDepartureDate)} → {oc.travelReturnDate ? formatDate(oc.travelReturnDate) : '—'}
            </dd>
          </div>
        ) : null}
      </dl>

      {referral.state === 'REFERRED' ? (
        <button className="btn-primary" onClick={() => run('/overseas/convene-board')} disabled={busy || !isBoard}>
          Convene board
        </button>
      ) : null}

      {referral.state === 'BOARD_REVIEW' ? (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <Field label="Destination country">
              <input className="field-input" value={destinationCountry} onChange={(e) => setDestinationCountry(e.target.value)} placeholder="e.g. India" />
            </Field>
            <Field label="Destination facility">
              <input className="field-input" value={destinationFacility} onChange={(e) => setDestinationFacility(e.target.value)} placeholder="e.g. Apollo Hospital" />
            </Field>
          </div>
          <Field label="Board notes">
            <textarea className="field-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
          <div className="flex gap-2">
            <button
              className="btn-success"
              onClick={() => run('/overseas/board-decision', { approve: true, notes, destinationCountry, destinationFacility })}
              disabled={busy || !isBoard || notes.trim().length < 3 || !destinationCountry.trim() || !destinationFacility.trim()}
            >
              Approve
            </button>
            <button
              className="btn-danger"
              onClick={() => run('/overseas/board-decision', { approve: false, notes })}
              disabled={busy || !isBoard || notes.trim().length < 3}
            >
              Decline
            </button>
          </div>
        </div>
      ) : null}

      {referral.state === 'FINANCE_APPROVAL' ? (
        <div className="space-y-2">
          <Field label="Financing amount (SCR)">
            <input type="number" min="0" step="0.01" className="field-input" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Field>
          <Field label="Notes">
            <textarea className="field-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
          <div className="flex gap-2">
            <button
              className="btn-success"
              onClick={() => run('/overseas/finance-decision', { approve: true, amount: Number.parseFloat(amount), notes })}
              disabled={busy || !isBoard || notes.trim().length < 3 || !amount}
            >
              Approve financing
            </button>
            <button
              className="btn-danger"
              onClick={() => run('/overseas/finance-decision', { approve: false, notes })}
              disabled={busy || !isBoard || notes.trim().length < 3}
            >
              Decline
            </button>
          </div>
        </div>
      ) : null}

      {referral.state === 'LOGISTICS' ? (
        <div className="space-y-3">
          <div>
            <div className="mb-1 text-xs font-semibold text-slate-500">Logistics checklist</div>
            {checklist.length === 0 ? <p className="text-xs text-slate-400">No checklist items yet.</p> : null}
            <ul className="space-y-1">
              {checklist.map((c, i) => (
                <li key={i} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={c.done}
                    disabled={!isBoard}
                    onChange={() => saveLogistics(checklist.map((x, xi) => (xi === i ? { ...x, done: !x.done } : x)))}
                  />
                  <span className={c.done ? 'text-slate-400 line-through' : 'text-slate-700'}>{c.item}</span>
                </li>
              ))}
            </ul>
            <div className="mt-2 flex gap-2">
              <input className="field-input" placeholder="Add checklist item…" value={newChecklistItem} onChange={(e) => setNewChecklistItem(e.target.value)} />
              <button
                className="btn-secondary shrink-0"
                disabled={!isBoard || !newChecklistItem.trim()}
                onClick={() => {
                  const next = [...checklist, { item: newChecklistItem.trim(), done: false }];
                  setNewChecklistItem('');
                  saveLogistics(next);
                }}
              >
                Add
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Departure date">
              <input type="date" className="field-input" value={departureDate} onChange={(e) => setDepartureDate(e.target.value)} />
            </Field>
            <Field label="Return date">
              <input type="date" className="field-input" value={returnDate} onChange={(e) => setReturnDate(e.target.value)} />
            </Field>
          </div>
          <button
            className="btn-secondary"
            disabled={!isBoard || (!departureDate && !returnDate)}
            onClick={() => run('/overseas/logistics', { travelDepartureDate: departureDate || undefined, travelReturnDate: returnDate || undefined })}
          >
            Save travel dates
          </button>
          <button
            className="btn-primary block"
            disabled={busy || !isBoard || checklist.length === 0 || !checklist.every((c) => c.done) || !oc.travelDepartureDate}
            onClick={() => run('/overseas/depart')}
          >
            Mark departed
          </button>
          {checklist.length === 0 || !checklist.every((c) => c.done) ? (
            <p className="text-xs text-amber-600">Every checklist item must be checked off before departure can be recorded.</p>
          ) : null}
        </div>
      ) : null}

      {referral.state === 'TREATMENT' ? (
        <button className="btn-primary" onClick={() => run('/overseas/complete-treatment')} disabled={busy || !isBoard}>
          Mark treatment complete
        </button>
      ) : null}

      {referral.state === 'FOLLOWUP' ? (
        <div className="space-y-2">
          {oc.followUpNotes ? (
            <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-700">{oc.followUpNotes}</div>
          ) : (
            <p className="text-xs text-slate-400">No follow-up notes yet.</p>
          )}
          <Field label="Add a follow-up note">
            <textarea className="field-input" rows={2} value={followupNote} onChange={(e) => setFollowupNote(e.target.value)} />
          </Field>
          <button
            className="btn-secondary"
            disabled={busy || !(isBoard || isClinician) || !followupNote.trim()}
            onClick={() => {
              void run('/overseas/followup-note', { note: followupNote });
              setFollowupNote('');
            }}
          >
            Save note
          </button>
        </div>
      ) : null}

      {referral.state === 'REJECTED' ? <p className="text-sm text-slate-500">This referral was declined. {oc.boardNotes}</p> : null}
    </div>
  );
}
